import type { TestAccounts } from '@felix/domain';
import { TestAccountsPanel } from '@felix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { IconArrowRight, IconShieldLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError, fetchTestAccounts, officeApi } from '../api.js';
import { AuthScreen } from '../components/AuthScreen.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { FormHeader } from '../components/FormHeader.js';
import { pathForStage } from '../routing.js';
import { useSession } from '../session.js';

const schema = z.object({
  email: z.string().trim().min(3).max(320),
  password: z.string().min(1).max(1024),
});
type Values = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  // Bağlı test ortamı (ADR 0092): sunucuda ayar açıksa ofis ve gemi test hesapları
  const [testAccounts, setTestAccounts] = useState<TestAccounts | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchTestAccounts().then((accounts) => {
      if (alive) setTestAccounts(accounts);
    });
    return () => {
      alive = false;
    };
  }, []);

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { stage } = await officeApi.login(values.email, values.password);
      session.setPendingPassword(stage === 'password_change_required' ? values.password : null);
      session.setStage(stage);
      await session.refresh();
      navigate(pathForStage(stage), { replace: true });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
    }
  });

  return (
    <AuthScreen
      footnote={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconShieldLock size={15} aria-hidden />
          {t('login.footnote')}
        </span>
      }
    >
      <FormHeader title={t('login.title')} subtitle={t('login.subtitle')} />
      {testAccounts ? (
        <TestAccountsPanel
          company={testAccounts.company.name}
          password={testAccounts.password}
          groups={[
            {
              key: 'office',
              title: t('testAccounts.office'),
              usable: true,
              accounts: testAccounts.office,
            },
            ...testAccounts.vessels.map((vessel) => ({
              key: vessel.id,
              title: vessel.name,
              note: t('testAccounts.signInOnBoard'),
              usable: false,
              accounts: vessel.crew,
            })),
          ]}
          onUse={(email, password) => {
            setError(null);
            form.setValue('email', email);
            form.setValue('password', password);
          }}
        />
      ) : null}
      <ErrorAlert code={error} />
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <TextInput
            label={t('login.email')}
            placeholder={t('login.emailPlaceholder')}
            type="email"
            autoComplete="username"
            autoFocus
            required
            withAsterisk={false}
            {...form.register('email')}
          />
          <PasswordInput
            label={t('login.password')}
            autoComplete="current-password"
            required
            withAsterisk={false}
            {...form.register('password')}
          />
          <Button
            type="submit"
            size="md"
            fullWidth
            mt="xs"
            loading={form.formState.isSubmitting}
            rightSection={<IconArrowRight size={18} />}
          >
            {t('login.submit')}
          </Button>
          {/* E-postayla sıfırlama yok: parolayı yönetici ya da crew manager sıfırlar (ortak 2026-10-30) */}
          <Text fz="sm" c="dimmed" ta="center">
            {t('login.forgot')}
          </Text>
        </Stack>
      </form>
    </AuthScreen>
  );
}
